import { useEffect, useState } from "react";
import { PlanInterval, Pocket, SubscriptionPlan } from "../../payment/SubscriptionApi";
import { Button, Col, Row, Spinner } from "react-bootstrap";
import logger, { LogTagKey } from "../../../utils/logger";
import { UserInfo } from "../../signup/SignUpApi";
import useApi from "../../../hooks/useApi";
import "./SubcriptionPlans.scss";
import { TripleSpinner } from "../../ui/TripleSpinner";
import { useTranslation } from "react-i18next";
import { SubscriptionPlanBenefit } from "./SubcriptionPlansBenefits";
import { getCreditAgencyListShort } from "../../../utils/country";
import { SubscriptionPlanInfo } from "./SubcriptionPlanInfo";
import { DetailSubscriptionPlanItem } from "./DetailSubscriptionPlanItem";
import { Link } from "react-router-dom";
import { ErrorModal } from "../../error/ErrorModal";
import { isCountryUk } from "../../../utils/country";
import { useContentfulContext } from "../../../context/ContentfulContext";

export interface ISelected {
  selected?: boolean;
}

const SubscriptionPlans = (props: {
  onSubscriptionPlanSelected: (plan: SubscriptionPlan | undefined) => void;
  setErrorMessage: (s: string | undefined) => void;
  pocket: Pocket | undefined;
  userInfo: UserInfo | undefined;
  currentPlanId?: string | undefined;
  isSignup: boolean;
  isSubmiting?: boolean;
  onSubmitBtnClick: (data: any, plan?: SubscriptionPlan) => void;
  showStoreLink?: boolean;
}) => {
  const { t, ready } = useTranslation();
  const {
    onSubscriptionPlanSelected,
    pocket,
    setErrorMessage,
    userInfo,
    currentPlanId,
    isSignup,
    isSubmiting,
    onSubmitBtnClick,
    showStoreLink,
  } = props;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState<(SubscriptionPlan & ISelected)[]>();
  const [preselectedPlan, setPreselectedPlan] = useState<SubscriptionPlan & ISelected>();
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan & ISelected>();
  const [currentActivePlan, setCurrentActivePlan] = useState<SubscriptionPlan>();
  const [error, setError] = useState<string>();
  const [showError, setShowError] = useState<boolean>(false);
  const { content, isLoading: isContentfulLoading } = useContentfulContext();

  const subscriptionApi = useApi().subscription;

  useEffect(() => {
    const fetchSubscriptions = async () => {
      if (pocket?.id) {
        setErrorMessage(undefined);
        setIsLoading(true);

        subscriptionApi
          .getSubscriptionPlans()
          .then((response) => {
            const showingIds =
              content?.subscriptionPlans === undefined ? [] : content.subscriptionPlans.allowShowingPlanIds;
            let preselectedPlan;
            const subscriptionPlans = response
              .filter((p) => {
                if (showingIds?.length > 0) {
                  return showingIds.includes(p.subscriptionPlanId);
                }

                return true;
              })
              .sort((p1: SubscriptionPlan, p2: SubscriptionPlan) => p1.order - p2.order)
              .map((p: SubscriptionPlan & ISelected) => {
                currentPlanId && p.stripePlanId === currentPlanId && setCurrentActivePlan(p);
                if (p.stripePlanId === currentPlanId || (!currentPlanId && p.isDefault)) {
                  p.selected = true;
                  preselectedPlan = p;
                }
                return p;
              });

            if (subscriptionPlans.length === 0) {
              throw new Error("No active subscription plans");
            }

            setSubscriptionPlans(subscriptionPlans);

            if (preselectedPlan) {
              setPreselectedPlan(preselectedPlan);
            }
          })
          .catch((e: any) => {
            showErrorMessage(t("common.error.unknown"));
            setIsLoading(false);
            logger.error(e, LogTagKey.TkSubscription, "Payment System error fetching subscription plans", {
              membershipNumber: userInfo?.membershipNumber,
              pocketId: pocket?.id,
            });
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    };
    fetchSubscriptions().then(() => {});
  }, [pocket]);

  useEffect(() => {
    preselectedPlan && onPlanSelected(preselectedPlan);
  }, [preselectedPlan]);

  const onPlanSelected = (selectedPlan: SubscriptionPlan | undefined) => {
    setSubscriptionPlans(
      (subscriptionPlans ?? []).map((plan) => ({
        ...plan,
        selected: plan.subscriptionPlanId === selectedPlan?.subscriptionPlanId,
      }))
    );
    onSubscriptionPlanSelected(selectedPlan);
    setSelectedPlan(selectedPlan);
    validateSelectedPlan(selectedPlan);
  };
  const validateSelectedPlan = (selectedPlan: SubscriptionPlan | undefined) => {
    if (!selectedPlan || !currentActivePlan) return;
    switch (currentActivePlan.interval) {
      case PlanInterval.year:
        if (selectedPlan.interval === PlanInterval.month) {
          showErrorMessage(t("subscription.errors.downgradeAnnualToMonthly"));
          return;
        }
        if (
          selectedPlan.interval === PlanInterval.year &&
          selectedPlan.renewalPeriod < currentActivePlan.renewalPeriod
        ) {
          showErrorMessage(t("subscription.errors.downgradePlan"));
          return;
        }
        setError(undefined);
        return;
      case PlanInterval.month:
        if (
          selectedPlan.interval === PlanInterval.month &&
          selectedPlan.renewalPeriod < currentActivePlan.renewalPeriod
        ) {
          showErrorMessage(t("subscription.errors.downgradePlan"));
          return;
        }
        setError(undefined);
        return;
      default:
        showErrorMessage(t("subscription.errors.unexpectedError"));
    }
  };
  const showErrorMessage = (message: string) => {
    setError(message);
    setShowError(true);
  };
  if (!ready) {
    return null;
  }

  if (isLoading || isContentfulLoading) {
    return <TripleSpinner />;
  }

  return (
    <>
      <div className="subscription-plans-hero text-center my-4">
        <h2 id="subscription-plans-title" className="plans-hero-title">
          {content?.subscriptionPlans?.title ?? t("settings.forms.plans.title")}
        </h2>
        <p className="plans-hero-subtitle">
          {t("subscription.subscriptionInfo.subHeaderBuildCreditShort", {
            /* Was a hard-coded "Equifax, Experian & TransUnion". The US only
               reports to Experian, so the subtitle named two agencies it does
               not use. `getCreditAgencyListShort` is the per-country list and
               is what every other caller of this string already uses. */
            creditAgencies: getCreditAgencyListShort(),
            defaultValue: "Access the Bits store and apply for your credit line with 0% APR.",
          })}
        </p>
        {showStoreLink && (
          <Link to="/store" className="plans-store-link" target="_blank">
            {t("settings.forms.plans.seeStore")} →
          </Link>
        )}
      </div>
      <Row className="mt-4 justify-content-center plans-row">
        {subscriptionPlans &&
          subscriptionPlans.map((subscriptionPlan: SubscriptionPlan & ISelected) => {
            const id = `plan${subscriptionPlan.subscriptionPlanId}`;
            const colWidth =
              (subscriptionPlans?.length ?? 1) === 1 ? { xs: 12, md: 8, lg: 6, xl: 5 } : { xs: 12, md: 6, lg: 4 };
            return (
              <Col
                className={`my-3 subscription-card-holder ${
                  /* `selected` alone: it is assigned here on load and
                     reassigned exclusively on every click, so OR-ing the
                     current plan's id back in only made that plan stay
                     highlighted alongside whichever one the user then chose. */
                  subscriptionPlan.selected ? "active" : ""
                }`}
                id={id}
                key={`col-${subscriptionPlan.subscriptionPlanId}`}
                {...colWidth}
              >
                <DetailSubscriptionPlanItem
                  key={subscriptionPlan.subscriptionPlanId}
                  isSignup={isSignup}
                  subscriptionPlan={subscriptionPlan}
                  currentPlanId={currentPlanId}
                  onSelect={() => onPlanSelected(subscriptionPlan)}
                  onSubmitBtnClick={onSubmitBtnClick}
                />
              </Col>
            );
          })}
      </Row>
      {!isSignup && (
        <>
          <SubscriptionPlanBenefit planBenefit={content?.subscriptionPlans?.benefits ?? []} />
          <Row className="d-none d-md-block text-center my-4">
            <Col>
              <Button
                id="changePlan"
                className="submit-button"
                variant="primary"
                disabled={selectedPlan?.stripePlanId === currentPlanId || !!error || isSubmiting}
                onClick={onSubmitBtnClick}
              >
                {isSubmiting && (
                  <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" className="me-2" />
                )}
                {selectedPlan?.stripePlanId === currentPlanId
                  ? t("subscription.forms.planChange.currentPlan")
                  : t("subscription.forms.planChange.changeButton")}
              </Button>
            </Col>
          </Row>
          {/* UK only. SPEED-3761 removed this block from the US change-plan
              page; the UK keeps it exactly as it was. (On the US it also
              highlighted the default plan's credit limit rather than the
              selected plan's.) */}
          {isCountryUk() && (
            <Row className="my-5 mx-2 justify-content-center">
              <SubscriptionPlanInfo
                description={content?.subscriptionPlans?.description || []}
                highlightedPlan={selectedPlan}
              />
            </Row>
          )}
        </>
      )}

      <ErrorModal
        id="modal-error-select-plan"
        show={showError}
        errorMessage={error}
        onClose={() => setShowError(false)}
      />
    </>
  );
};

export default SubscriptionPlans;
